import React, { forwardRef } from 'react';
import { ICON_MAP, type IconName, type IconWeight } from 'shared/constants/iconMap';
import { cn } from 'shared/utils/cn';

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  iconWeight?: IconWeight;
  variant?: 'primary' | 'secondary' | 'inverted' | 'gold' | 'ghost' | 'link';
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeConfig: Record<
  NonNullable<IconButtonProps['size']>,
  { iconSize: number; padding: string }
> = {
  sm: { iconSize: 16, padding: 'p-2' },
  md: { iconSize: 20, padding: 'p-2.5' },
  lg: { iconSize: 24, padding: 'p-3' },
  xl: { iconSize: 32, padding: 'p-4' },
};

const variantClasses: Record<NonNullable<IconButtonProps['variant']>, string> = {
  primary: 'bg-btn-primary text-btn-primary-text',
  secondary: 'bg-btn-secondary text-btn-secondary-text',
  inverted: 'bg-btn-inverted text-btn-inverted-text',
  gold: 'bg-tertiary text-on-tertiary font-bold',
  ghost: 'border border-outline-variant/20 text-on-surface hover:bg-primary-container',
  link: 'text-tertiary font-bold',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, iconWeight, variant = 'primary', size = 'md', className, disabled, ...props }, ref) => {
    const { iconSize, padding } = sizeConfig[size];
    const IconComponent = ICON_MAP[icon];

    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'rounded-full font-sans transition-transform duration-200',
          'hover:scale-[1.02] active:scale-[0.98]',
          'flex items-center justify-center',
          'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
          variantClasses[variant],
          padding,
          disabled && 'opacity-[0.38] pointer-events-none',
          className,
        )}
        {...props}
      >
        <IconComponent size={iconSize} weight={iconWeight} />
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
